import styled from 'styled-components';
import tw from 'twin.macro';
import {
  formatDateToString,
  getHomeTypeLanguageString,
  getOwnershipLanguageString,
} from '../helpers';
import { Tag, TagColorsByIndex } from './Tag';
import strings from '../language/Swedish';
import useIsMobile from '@/helpers/useIsMobile';

import { FC, Fragment } from 'react';

const HomeType = styled.div`
  ${tw`mt-3 flex-col items-end gap-1 hidden md:flex`};
  display: flex;
  flex-direction: column;
  width: 100%;
  flex-wrap: wrap;
  float: right;
  justify-content: flex-end;
`;

interface Filters {
  home_type: string[] | null;
  ad_advertising_days_min: number | null | undefined;
  ad_advertising_days_max: number | null | undefined;
  construction_year_min: number | null;
  construction_year_max: number | null;
  ownership_type: string[] | null;
  living_area_min: number | null;
  living_area_max: number | null;
  registered_date_min: Date | null | undefined;
  registered_date_max: Date | null | undefined;
  contract_date_min: Date | null | undefined;
  contract_date_max: Date | null | undefined;
  price_min: number | null;
  price_max: number | null;
  monthly_fee_min: number | null | undefined;
  monthly_fee_max: number | null | undefined;
  floor_min: number | null | undefined;
  floor_max: number | null | undefined;
  rooms_min: number | null;
  rooms_max: number | null;
  orgNr: boolean | null;
  firm: boolean | null;
  estate_agencies: string | null;
  owner_id: boolean | null;
  shore_distance_min: number | null;
  shore_distance_max: number | null;
}

interface Props {
  filterData: Partial<Filters>;
}

const HomeTypeTagContainer: FC<Props> = ({ filterData }) => {
  const homeTypeString = String(filterData.home_type ?? '');
  const homeTypeArray = homeTypeString.split(',');
  const isMobile = useIsMobile();
  //same filters everywhere
  const hasEnabledLivingArea = filterData.living_area_min !== null;
  const hasEnabledPrice = filterData.price_min !== null;
  const hasEnabledRooms = filterData.rooms_min !== null;
  const hasEnabledConstructionDate = filterData.construction_year_min !== null;

  //optional filters
  const hasEnabledAdvertisingDays =
    filterData.ad_advertising_days_min !== null &&
    filterData.ad_advertising_days_min !== undefined;
  const hasEnabledMonthlyFee =
    filterData.monthly_fee_min !== null &&
    filterData.monthly_fee_min !== undefined;
  const hasEnabeledFloor =
    filterData.floor_min !== null &&
    filterData.floor_min !== undefined;
  const hasEnabledContractDate =
    filterData.contract_date_min !== null &&
    filterData.contract_date_min !== undefined;
  const hasEnabledRegisteredDate =
    filterData.registered_date_min !== null &&
    filterData.registered_date_min !== undefined;

  const hasEnabledShoreDistance =
    filterData.shore_distance_min !== null &&
    filterData.shore_distance_max !== undefined;
  const hasEnabledOrg =
    filterData.orgNr !== null && filterData.orgNr !== undefined;

  const hasEnabledEstateAgencies =
    filterData.firm !== null && filterData.estate_agencies !== undefined;

  const filters = [];

  const ownerIdString = filterData.owner_id ? `: ${filterData.owner_id}` : '';
  const estateAgenciesString = filterData.estate_agencies ? `: ${filterData.estate_agencies}` : '';

  if (hasEnabledPrice) {
    filters.push({
      id: 'price',
      label: `${strings.filter.price}: ${filterData.price_min}-${filterData.price_max} ${strings.filter.price_unit}`,
    });
  }
  if (hasEnabledLivingArea) {
    filters.push({
      id: 'living_area',
      label: `${strings.filter.living_area}: ${filterData.living_area_min}-${filterData.living_area_max} ${strings.filter.living_area_unit}`,
    });
  }
  if (hasEnabledMonthlyFee) {
    filters.push({
      id: 'monthly_fee',
      label: `${strings.filter.monthly_fee}: ${filterData.monthly_fee_min}-${filterData.monthly_fee_max} ${strings.filter.monthly_fee_unit}`,
    });
  }
  if (hasEnabledRooms) {
    filters.push({
      id: 'room',
      label: `${strings.filter.rooms}: ${filterData.rooms_min}-${filterData.rooms_max} ${strings.filter.rooms_unit}`,
    });
  }
  if (hasEnabledOrg && filterData.orgNr) {
    filters.push({
      id: 'orgNr',
      label: `${strings.filter.orgNr}${ownerIdString}`,
    });
  }

  if (hasEnabledEstateAgencies && filterData.estate_agencies) {
    filters.push({
      id: 'estate_agencies',
      label: `${strings.filter.checkboxItems.firm}${estateAgenciesString}`,
    });
  }

  if (hasEnabledConstructionDate) {
    filters.push({
      id: 'constructionYear',
      label: `${strings.filter.construction_year}: ${filterData.construction_year_min}-${filterData.construction_year_max}`,
    });
  }

  if (hasEnabledAdvertisingDays) {
    filters.push({
      id: 'advertisingDays',
      label: `${strings.filter.advertisingDays}: ${filterData.ad_advertising_days_min}-${filterData.ad_advertising_days_max}`,
    });
  }
  if (hasEnabeledFloor) {
    filters.push({
      id: 'floor',
      label: `${strings.filter.floor}: ${filterData.floor_min}-${filterData.floor_max}`,
    });
  }

  if (hasEnabledContractDate) {
    const min = filterData.contract_date_min;
    const max = filterData.contract_date_max;
    const label = `${strings.filter.contract_date}: ${formatDateToString(min as Date)}${max?.toString() !== 'Invalid Date' ? '-' + formatDateToString(max as Date) : ''}`;
    filters.push({
      id: 'contractDate',
      label: label,
    });
  }

  if (hasEnabledRegisteredDate) {
    filters.push({
      id: 'registeredDate',
      label: `${strings.filter.registeredDate}: ${formatDateToString(
        filterData.registered_date_min as Date
      )}-${formatDateToString(filterData.registered_date_max as Date)}`,
    });
  }

  if (hasEnabledShoreDistance) {
    filters.push({
      id: 'shoreDistance',
      label: `${strings.filter.shore}: ${filterData.shore_distance_min}-${filterData.shore_distance_max}`,
    });
  }

  return (
		!isMobile && (
			<HomeType>
				<Tag
					id='homeTypeArray'
					color={TagColorsByIndex[1]}
					label={homeTypeArray.map((homeType: string, index: number) =>
						homeType.trim().length > 0
							? `${getHomeTypeLanguageString(homeType)}${
									homeTypeArray.length - 1 !== index ? ' | ' : ''
							  }`
							: ''
					)}
				/>
				{filterData.ownership_type && (
					<Tag
						id='ownership_type'
						color={TagColorsByIndex[2]}
						label={filterData?.ownership_type.map(
							(homeType: string, index: number) =>
								homeType.trim().length > 0
									? `${getOwnershipLanguageString(homeType)}${
											filterData?.ownership_type.length - 1 !== index
												? ' | '
												: ''
									  }`
									: ''
						)}
					/>
				)}
				{filters.map((filter, index) => {
					return (
						<Tag
							key={`tag-${filter.id}`}
							id={filter.id}
							label={[String(filter.label)]}
							color={TagColorsByIndex[index]}
						/>
					);
				})}
			</HomeType>
		)
	);
};

export default HomeTypeTagContainer;
